<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SettingsLayout from 'dashboard/components-next/Settings/SettingsLayout.vue';
import IntegrationEmptyState from '../shared/IntegrationEmptyState.vue';
import IntegrationConnectionCard from '../shared/IntegrationConnectionCard.vue';
import DashboardAppModal from './DashboardAppModal.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const showDashboardAppPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedApp = ref({});
const mode = ref('CREATE');

const records = computed(() => getters['dashboardApps/getRecords'].value || []);
const uiFlags = computed(() => getters['dashboardApps/getUIFlags'].value || {});
const isLoading = computed(() => uiFlags.value.isFetching);
const isEmpty = computed(() => !records.value.length);

onMounted(() => {
  store.dispatch('dashboardApps/get');
});

const openCreatePopup = () => {
  mode.value = 'CREATE';
  selectedApp.value = {};
  showDashboardAppPopup.value = true;
};

const toggleDashboardAppPopup = () => {
  showDashboardAppPopup.value = !showDashboardAppPopup.value;
  selectedApp.value = {};
};

const editApp = app => {
  mode.value = 'UPDATE';
  selectedApp.value = app;
  showDashboardAppPopup.value = true;
};

const openDeletePopup = app => {
  selectedApp.value = app;
  showDeleteConfirmationPopup.value = true;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const confirmDeletion = async () => {
  try {
    await store.dispatch('dashboardApps/delete', selectedApp.value.id);
    useAlert(t('INTEGRATION_SETTINGS.DASHBOARD_APPS.DELETE.API_SUCCESS'));
    closeDeletePopup();
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.DASHBOARD_APPS.DELETE.API_ERROR'));
  }
};
</script>

<template>
  <SettingsLayout
    :header-title="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.TITLE')"
    :button-label="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.HEADER_BTN_TXT')"
    @click="openCreatePopup"
  >
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Spinner />
    </div>

    <IntegrationEmptyState
      v-else-if="isEmpty"
      icon="i-lucide-layout-dashboard"
      :title="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.LIST.404')"
      :description="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.DESCRIPTION')"
      :button-label="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.HEADER_BTN_TXT')"
      button-icon="i-lucide-plus"
      @action="openCreatePopup"
    />

    <div v-else class="flex flex-col gap-4">
      <p class="text-sm text-n-slate-11">
        {{ $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.DESCRIPTION') }}
      </p>
      <IntegrationConnectionCard
        v-for="app in records"
        :key="app.id"
        icon="i-lucide-layout-dashboard"
        icon-class="bg-gradient-to-br from-slate-500 to-slate-700"
        :title="app.title"
        :description="app.content?.[0]?.url || ''"
        :show-status="false"
      >
        <template #actions>
          <Button
            v-tooltip.top="
              $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.LIST.EDIT_TOOLTIP')
            "
            icon="i-lucide-pen"
            slate
            faded
            size="sm"
            @click="editApp(app)"
          />
          <Button
            v-tooltip.top="
              $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.LIST.DELETE_TOOLTIP')
            "
            icon="i-lucide-trash-2"
            ruby
            faded
            size="sm"
            @click="openDeletePopup(app)"
          />
        </template>
      </IntegrationConnectionCard>
    </div>

    <DashboardAppModal
      v-if="showDashboardAppPopup"
      :show="showDashboardAppPopup"
      :mode="mode"
      :selected-app-data="selectedApp"
      @close="toggleDashboardAppPopup"
    />

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.DELETE.TITLE')"
      :message="
        $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.DELETE.MESSAGE', {
          appName: selectedApp.title,
        })
      "
      :confirm-text="
        $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.DELETE.CONFIRM_YES')
      "
      :reject-text="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.DELETE.CONFIRM_NO')"
    />
  </SettingsLayout>
</template>
